import React, { useState, useCallback } from "react";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { IconSearch, IconLoader2, IconShieldLock, IconUser, IconChevronRight } from "@tabler/icons-react";
import { cpAdminSearchApplications, cpAdminMasquerade } from "_graphql/mutations";
import { usePortalSettings } from "_contexts/usePortalSettings";
import debounce from "lodash/debounce";
import "./AdminSelectApplication.scss";

// Background images
import FieldsImage from "_assets/images/customer-portal/fields.jpg";
import HillsImage from "_assets/images/customer-portal/hills.jpg";
import MountainsImage from "_assets/images/customer-portal/mountains.jpg";
import PoppiesImage from "_assets/images/customer-portal/poppies.jpg";
import SunsetImage from "_assets/images/customer-portal/sunset.jpg";

const backgroundImages = {
  fields: FieldsImage,
  hills: HillsImage,
  mountains: MountainsImage,
  poppies: PoppiesImage,
  sunset: SunsetImage,
};

function AdminSelectApplication({ adminToken, onSuccess, onLogout }) {
  const client = useApolloClient();
  const { settings, companyId } = usePortalSettings();

  const [search, setSearch] = useState("");
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);

  // Get company settings
  const logoUrl = settings?.logo_url;
  const companyName = settings?.company?.name || "Paypath";
  const backgroundImage = settings?.background_image || "poppies";
  const bgImageUrl = backgroundImages[backgroundImage] || backgroundImages.poppies;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchApplications = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setApplications([]);
        setTotal(0);
        return;
      }

      setLoading(true);

      try {
        const result = await client.mutate({
          mutation: cpAdminSearchApplications,
          variables: {
            token: adminToken,
            company_id: companyId,
            search: searchTerm,
            limit: 20,
          },
        });

        const searchResult = result?.data?.cpAdminSearchApplications;

        if (searchResult?.success) {
          setApplications(searchResult.applications || []);
          setTotal(searchResult.total || 0);
        } else {
          if (searchResult?.error?.includes("expired")) {
            message.error("Session expired. Please login again.");
            onLogout();
            return;
          }
          message.error(searchResult?.error || "Search failed");
          setApplications([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("Search error:", err);
        message.error("Search failed. Please try again.");
        setApplications([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }, 300),
    [adminToken, companyId, client, onLogout]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    searchApplications(value);
  };

  const handleSelectApplication = async (application) => {
    setSelecting(application.id);

    try {
      const result = await client.mutate({
        mutation: cpAdminMasquerade,
        variables: {
          token: adminToken,
          application_id: application.id,
        },
      });

      const masquerade = result?.data?.cpAdminMasquerade;

      if (masquerade?.success && masquerade?.token) {
        message.success(`Logged in as ${application.first_name} ${application.last_name}`);
        onSuccess(masquerade.token);
      } else {
        if (masquerade?.error?.includes("expired")) {
          message.error("Session expired. Please login again.");
          onLogout();
          return;
        }
        message.error(masquerade?.error || "Failed to masquerade");
        setSelecting(null);
      }
    } catch (err) {
      console.error("Masquerade error:", err);
      message.error("Failed to log in. Please try again.");
      setSelecting(null);
    }
  };

  return (
    <div className="admin-select-container">
      {/* Background with blur */}
      <div
        className="auth-background"
        style={{ backgroundImage: `url(${bgImageUrl})` }}
      />

      {/* Modal */}
      <div className="auth-modal">
        <div className="auth-content">
          {/* Brand Icon */}
          {logoUrl && (
            <div className="brand-icon">
              <img src={logoUrl} alt={companyName} />
            </div>
          )}

          {/* Admin Badge */}
          <div className="admin-badge">
            <IconShieldLock size={16} />
            <span>Admin Access</span>
          </div>

          {/* Title Section */}
          <div className="welcome-section">
            <h1 className="welcome-title">
              Select Customer
              <br />
              Account
            </h1>
            <p className="welcome-subtitle">
              Search for a subscribed customer to access their portal
            </p>
          </div>

          {/* Search Section */}
          <div className="input-section">
            <div className="search-input-wrapper">
              <IconSearch className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, phone, or ID..."
                value={search}
                onChange={handleSearchChange}
                autoFocus
              />
              {loading && (
                <IconLoader2 className="loading-icon" size={18} />
              )}
            </div>

            {/* Results List */}
            {applications.length > 0 && (
              <div className="results-section">
                <div className="results-header">
                  <span className="results-count">
                    {total > 20 ? `Showing 20 of ${total} results` : `${total} result${total !== 1 ? "s" : ""} found`}
                  </span>
                </div>
                <div className="results-list">
                  {applications.map((app) => (
                    <button
                      key={app.id}
                      className={`result-item ${selecting === app.id ? "selecting" : ""}`}
                      onClick={() => handleSelectApplication(app)}
                      disabled={selecting !== null}
                    >
                      <div className="result-avatar">
                        {selecting === app.id ? (
                          <IconLoader2 className="avatar-loading" size={18} />
                        ) : (
                          <IconUser size={18} />
                        )}
                      </div>
                      <div className="result-info">
                        <span className="result-name">
                          {app.first_name} {app.last_name}
                        </span>
                        <span className="result-email">{app.email}</span>
                      </div>
                      <IconChevronRight className="result-arrow" size={18} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {search.length >= 2 && !loading && applications.length === 0 && (
              <div className="no-results">
                <p>No subscribed customers found matching "{search}"</p>
              </div>
            )}

            {/* Search hint */}
            {search.length > 0 && search.length < 2 && (
              <p className="search-hint">Enter at least 2 characters to search</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="auth-footer">
          <button className="signout-button" onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminSelectApplication;
